import { DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { isDevMode, InjectionToken, Injectable, Optional, Inject } from '@angular/core';
import * as i1 from '@noda-lib/utils/services';
class CookiesEnabledBaseManager {
  has(name) {
    name = encodeURIComponent(name);
    const regExp = this.getCookieRegExp(name);
    const cookies = this.getNativeCookies();
    return regExp.test(cookies);
  }
  get(name) {
    if (this.has(name)) {
      name = encodeURIComponent(name);
      const regExp = this.getCookieRegExp(name);
      const cookies = this.getNativeCookies();
      const result = regExp.exec(cookies);
      return result[1] ? decodeURIComponent(result[1]) : '';
    } else {
      return '';
    }
  }
  getAll() {
    const cookies = this.getNativeCookies();
    let decodedCookies = {};
    if (Boolean(cookies)) {
      decodedCookies = cookies.split(';').reduce((accumulator, cookieItem) => {
        const [cookieName, cookieValue] = cookieItem.split('=');
        const name = decodeURIComponent(cookieName.trim());
        const value = decodeURIComponent(cookieValue);
        accumulator[name] = value;
        return accumulator;
      }, {});
    }
    return decodedCookies;
  }
  getCookieRegExp(name) {
    const escapedName = name.replace(/([\[\]{}()|=;+?,.*^$])/gi, '\\$1');
    return new RegExp(`(?:^${escapedName}|;\\s*${escapedName})=(.*?)(?:;|$)`, 'g');
  }
}
class CookiesDisabledManager {
  has(name) {
    this.log();
    return false;
  }
  get(name) {
    this.log();
    return '';
  }
  getAll() {
    this.log();
    return {};
  }
  set(name, value, options) {
    this.log();
  }
  delete(name, sameSite, path, domain, secure) {
    this.log();
  }
  deleteAll(sameSite, path, domain, secure) {
    this.log();
  }
  log() {
    if (isDevMode()) {
      console.warn('The cookies are disabled');
    }
  }
}
class CookiesBuilder {
  constructor(name, value) {
    this.cookiesString = null;
    this.cookiesString = `${encodeURIComponent(name)}=${encodeURIComponent(value)};`;
  }
  setExpires(value) {
    if (value) {
      if (typeof value === 'number') {
        const dateExpires = new Date(new Date().getTime() + value * 1000 * 60 * 60 * 24);
        this.cookiesString += `expires=${dateExpires.toUTCString()};`;
      } else {
        this.cookiesString += `expires=${value.toUTCString()};`;
      }
    }
    return this;
  }
  setPath(value) {
    if (value) {
      this.cookiesString += `path=${value};`;
    }
    return this;
  }
  setDomain(value) {
    if (value) {
      this.cookiesString += `domain=${value};`;
    }
    return this;
  }
  setForceSecure(cookieName, options) {
    const forceCondition = options.secure === false && options.sameSite === 'None'; // eslint-disable-line
    if (forceCondition) {
      options.secure = true;
      console.warn(`[cookie-service] Cookie ${cookieName} was forced with secure flag because sameSite=None.`);
    }
    return this;
  }
  setSecure(value) {
    if (value) {
      this.cookiesString += 'secure;';
    }
    return this;
  }
  setSameSite(value = 'Lax') {
    this.cookiesString += `sameSite=${value};`;
    return this;
  }
  setPartitioned(value) {
    if (value) {
      this.cookiesString += 'Partitioned;';
    }
    return this;
  }
  build() {
    return this.cookiesString;
  }
}
class CookiesEnabledBrowserManager extends CookiesEnabledBaseManager {
  constructor(document) {
    super();
    this.document = document;
  }
  set(name, value, options = {}) {
    this.document.cookie = new CookiesBuilder(name, value).setExpires(options.expires).setPath(options.path).setDomain(options.domain).setForceSecure(name, options).setSecure(options.secure).setSameSite(options.sameSite).setPartitioned(options.partitioned).build();
  }
  delete(name, sameSite = 'Lax', path, domain, secure) {
    const expires = new Date(0);
    this.set(name, '', {
      expires,
      sameSite,
      path,
      domain,
      secure
    });
  }
  deleteAll(sameSite = 'Lax', path, domain, secure) {
    const cookies = this.getAll();
    for (const cookieName in cookies) {
      if (cookies.hasOwnProperty(cookieName)) {
        this.delete(cookieName, sameSite, path, domain, secure);
      }
    }
  }
  getNativeCookies() {
    return this.document.cookie;
  }
}
const COOKIES_SSR_MANAGER = new InjectionToken('COOKIES_SSR_MANAGER');
let CookiesService = /*#__PURE__*/(() => {
  class CookiesService {
    constructor(platformService, ssrManager, document) {
      this.platformService = platformService;
      this.ssrManager = ssrManager;
      this.document = document;
      this.cookiesManager = null;
      this.init();
    }
    init() {
      const isBrowserFlow = this.platformService.isBrowser() && this.document.defaultView.navigator.cookieEnabled;
      const isSsrFlow = this.platformService.isServer();
      switch (true) {
        case isBrowserFlow:
          {
            this.cookiesManager = new CookiesEnabledBrowserManager(this.document);
            break;
          }
        case isSsrFlow:
          {
            if (!this.ssrManager) throw new Error('SSR Cookies Manager is not provided. Use provideUtilsCookies(withSsrCookies())');
            this.cookiesManager = this.ssrManager;
            break;
          }
        default:
          this.cookiesManager = new CookiesDisabledManager();
      }
    }
    has(name) {
      return this.cookiesManager.has(name);
    }
    get(name) {
      return this.cookiesManager.get(name);
    }
    getAll() {
      return this.cookiesManager.getAll();
    }
    set(name, value, options = {}) {
      this.cookiesManager.set(name, value, options);
    }
    delete(name, sameSite = 'Lax', path, domain, secure) {
      this.cookiesManager.delete(name, sameSite, path, domain, secure);
    }
    deleteAll(sameSite = 'Lax', path, domain, secure) {
      this.cookiesManager.deleteAll(sameSite, path, domain, secure);
    }
    static {
      this.ɵfac = function CookiesService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CookiesService)(i0.ɵɵinject(i1.PlatformService), i0.ɵɵinject(COOKIES_SSR_MANAGER, 8), i0.ɵɵinject(DOCUMENT));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CookiesService,
        factory: CookiesService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CookiesService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function provideUtilsCookies(...features) {
  return [CookiesService, ...features];
}

/**
 * Generated bundle index. Do not edit.
 */

export { COOKIES_SSR_MANAGER, CookiesEnabledBaseManager, CookiesService, provideUtilsCookies };
